import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { FaTrash, FaMinus, FaPlus, FaArrowLeft, FaShoppingCart, FaEdit, FaTruck, FaStore } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import NavBar from '../components/nav';
import { getDishSupplements } from '../services/supplementApi';
import { getRestaurantById } from '../services/api';
import { calculateFinalTotal, getDeliveryInfo } from '../utils/deliveryUtils';
import namkeFallback from '../assets/namke-fallback.png';

const Cart = () => {
  const { t } = useTranslation();
  const { cartItems, removeFromCart, deleteFromCart, updateQuantity, clearCart, updateCartItem } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [orderType, setOrderType] = useState('delivery');
  const [acceptPickup, setAcceptPickup] = useState(false);
  const [restaurantSettings, setRestaurantSettings] = useState({});
  const [restaurantName, setRestaurantName] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [editSupplements, setEditSupplements] = useState([]);
  const [availableSupplements, setAvailableSupplements] = useState([]);
  const [supplementsLoading, setSupplementsLoading] = useState(false);

  // Charger les infos du restaurant (accept_pickup + delivery settings)
  useEffect(() => {
    const restaurantId = cartItems[0]?.restaurant_id;
    if (!restaurantId) return;
    getRestaurantById(restaurantId)
      .then(r => {
        setAcceptPickup(!!r?.accept_pickup);
        setRestaurantSettings({
          delivery_fee: r?.delivery_fee,
          minimum_order: r?.minimum_order,
        });
        setRestaurantName(r?.name || '');
      })
      .catch(() => {});
  }, [cartItems]);

  // Calculer le total du panier
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const itemTotal = (item.price + (item.supplements?.reduce((sum, sup) => sum + (sup.price * sup.quantity), 0) || 0)) * item.quantity;
      return total + itemTotal;
    }, 0);
  };

  const subtotal = calculateTotal();
  const deliveryCalculation = calculateFinalTotal(subtotal, cartItems, restaurantSettings);
  const { deliveryFee: rawDeliveryFee, total: rawTotal, isFreeDelivery } = deliveryCalculation;
  const deliveryFee = orderType === 'pickup' ? 0 : rawDeliveryFee;
  const finalTotal = orderType === 'pickup' ? subtotal : rawTotal;
  const deliveryInfo = orderType === 'delivery' ? getDeliveryInfo(subtotal, cartItems, restaurantSettings) : null;

  // Gérer la suppression complète d'un article
  const handleDeleteItem = (item) => {
    deleteFromCart(item);
  };

  // Gérer la mise à jour de la quantité
  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(item.id, newQuantity);
    } else {
      deleteFromCart(item);
    }
  };

  // Gérer la validation du panier
  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/checkout', { state: { orderType, acceptPickup, restaurantSettings } });
    }, 1000);
  };

  // Gérer la suppression de tous les articles
  const handleClearCart = () => {
    if (window.confirm(t('cart.confirmClear'))) {
      clearCart();
    }
  };

  // Gérer la modification d'un article
  const handleEditItem = async (item) => {
    setEditingItem(item);
    setEditQuantity(item.quantity);
    setEditSupplements(item.supplements || []);
    
    // Charger les suppléments disponibles pour ce plat
    try {
      setSupplementsLoading(true);
      const supplementsData = await getDishSupplements(item.id);
      setAvailableSupplements(supplementsData);
    } catch (error) {
      console.error('Erreur lors du chargement des suppléments:', error);
      setAvailableSupplements([]);
    } finally {
      setSupplementsLoading(false);
    }
  };

  // Gérer la sélection/désélection des suppléments
  const handleSupplementToggle = (supplement) => {
    const isSelected = editSupplements.some(s => s.id === supplement.id);
    
    if (isSelected) {
      setEditSupplements(prev => prev.filter(s => s.id !== supplement.id));
    } else {
      setEditSupplements(prev => [...prev, {
        id: supplement.id,
        name: supplement.name,
        price: supplement.price,
        quantity: 1
      }]);
    }
  };

  // Gérer la quantité des suppléments
  const handleSupplementQuantityChange = (supplementId, newQuantity) => {
    if (newQuantity >= 1) {
      setEditSupplements(prev => prev.map(s => 
        s.id === supplementId 
          ? { ...s, quantity: newQuantity }
          : s
      ));
    } else if (newQuantity === 0) {
      setEditSupplements(prev => prev.filter(s => s.id !== supplementId));
    }
  };

  const handleSaveEdit = () => {
    if (editingItem) {
      const updatedItem = {
        ...editingItem,
        quantity: editQuantity,
        supplements: editSupplements
      };
      updateCartItem(editingItem.id, updatedItem);
      setEditingItem(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-lg p-8">
              <div className="text-gray-400 mb-6">
                <FaShoppingCart className="w-24 h-24 mx-auto" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">{t('cart.emptyTitle')}</h1>
              <p className="text-gray-900 font-normal mb-8">
                {t('cart.emptySubtitle')}
              </p>
              <Link
                to="/"
                className="cart-page-cta inline-flex items-center px-6 py-3 font-semibold rounded-lg"
              >
                <FaArrowLeft className="mr-2" />
                {t('cart.continueShopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-800">{t('cart.title')}</h1>
            <button
              onClick={handleClearCart}
              className="text-red-600 hover:text-red-700 font-medium flex items-center"
            >
              <FaTrash className="mr-2" />
              {t('cart.clearCart')}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Liste des articles */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {t('cart.itemsCount', { count: cartItems.length })}
                  </h2>
                </div>
                
                <div className="divide-y divide-gray-200">
                  {cartItems.map((item) => (
                    <div key={item.id} className="p-6">
                      <div className="flex items-start space-x-4">
                        {/* Image du plat */}
                        <div className="flex-shrink-0 w-20 h-20">
                          <img
                            src={item.image || namkeFallback}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>

                        {/* Informations du plat */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                                {item.name}
                              </h3>
                              <p className="text-sm text-gray-900 font-normal mb-2">
                                {restaurantName || item.restaurantName}
                              </p>
                              
                              {/* Suppléments */}
                              {item.supplements && item.supplements.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs text-gray-900 font-normal mb-1">{t('cart.supplementsLabel')}</p>
                                  <div className="flex flex-wrap gap-1">
                                    {item.supplements.map((supplement, index) => (
                                      <span
                                        key={supplement.id || index}
                                        className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                                      >
                                        {supplement.name} x{supplement.quantity}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Prix unitaire */}
                            <div className="text-right ml-4">
                              <p className="text-lg font-semibold text-[rgb(5, 70, 37)]">
                                {((item.price + (item.supplements?.reduce((sum, sup) => sum + (sup.price * sup.quantity), 0) || 0)) * item.quantity).toFixed(2)} €
                              </p>
                              <p className="text-sm text-gray-900 font-normal">
                                {t('cart.pricePerUnit', { price: item.price.toFixed(2) })}
                              </p>
                            </div>
                          </div>

                          {/* Contrôles de quantité */}
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center"
                              >
                                <FaMinus className="w-3 h-3" />
                              </button>
                              <span className="text-lg font-medium w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center"
                              >
                                <FaPlus className="w-3 h-3" />
                              </button>
                            </div>

                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-[rgb(5, 70, 37)] hover:text-green-700 p-2 rounded-full hover:bg-green-50 transition-colors border border-green-200 hover:border-green-300"
                                title={t('cart.editItemTitle')}
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item)}
                                className="text-red-600 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                                title={t('cart.deleteItemTitle')}
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Résumé et checkout */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg p-6 sticky top-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">{t('cart.orderSummary')}</h2>

                {/* Sélecteur livraison / retrait */}
                {acceptPickup && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-2">{t('cart.receptionMode')}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setOrderType('delivery')}
                        className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                          orderType === 'delivery'
                            ? 'border-green-600 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-900 hover:border-gray-300'
                        }`}
                      >
                        <FaTruck className="w-4 h-4" />
                        {t('cart.delivery')}
                      </button>
                      <button
                        onClick={() => setOrderType('pickup')}
                        className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                          orderType === 'pickup'
                            ? 'border-green-600 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-900 hover:border-gray-300'
                        }`}
                      >
                        <FaStore className="w-4 h-4" />
                        {t('cart.pickup')}
                      </button>
                    </div>
                    {orderType === 'pickup' && (
                      <p className="text-xs text-green-600 mt-2">
                        {t('cart.pickupNote')}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-900 font-normal">
                    <span>{t('cart.subtotal')}</span>
                    <span>{subtotal.toFixed(2)} €</span>
                  </div>

                  {orderType === 'delivery' && (
                    <div className="flex justify-between text-gray-900 font-normal">
                      <span>{t('cart.deliveryFee')}</span>
                      <span>
                        {isFreeDelivery ? (
                          <span className="text-[rgb(5, 70, 37)] font-medium">{t('cart.free')}</span>
                        ) : (
                          `${deliveryFee.toFixed(2)} €`
                        )}
                      </span>
                    </div>
                  )}

                  {orderType === 'pickup' && (
                    <div className="flex justify-between text-gray-900 font-normal">
                      <span>{t('cart.deliveryFee')}</span>
                      <span className="text-[rgb(5, 70, 37)] font-medium">{t('cart.free')}</span>
                    </div>
                  )}

                  {deliveryInfo && (
                    <div className="text-sm text-[rgb(5, 70, 37)] bg-green-50 p-3 rounded-lg">
                      <p className="font-medium">{deliveryInfo.message}</p>
                      <p className="text-xs mt-1">{deliveryInfo.remainingText}</p>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-lg font-bold text-gray-800">
                      <span>{t('cart.total')}</span>
                      <span>{finalTotal.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={isLoading || cartItems.length === 0}
                  className="cart-page-cta w-full py-3 rounded-lg font-semibold flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('cart.processing')}
                    </>
                  ) : (
                    t('cart.placeOrder')
                  )}
                </button>

                <div className="mt-4 text-center">
                  <Link
                    to="/"
                    className="text-[rgb(5, 70, 37)] hover:text-green-700 font-medium flex items-center justify-center"
                  >
                    <FaArrowLeft className="mr-2" />
                    {t('cart.continueShopping')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup de modification */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">{t('cart.editItemTitleModal')}</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-900 font-normal mb-2">{t('cart.articleLabel')} <span className="font-medium text-gray-800">{editingItem.name}</span></p>
            </div>

            <div className="mb-4">
              <label htmlFor="editQuantity" className="block text-sm font-medium text-gray-700 mb-2">{t('cart.quantityLabel')}</label>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setEditQuantity(Math.max(1, editQuantity - 1))}
                  className="w-8 h-8 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center"
                >
                  <FaMinus className="w-3 h-3" />
                </button>
                <span className="text-lg font-medium w-12 text-center">{editQuantity}</span>
                <button
                  onClick={() => setEditQuantity(editQuantity + 1)}
                  className="w-8 h-8 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center"
                >
                  <FaPlus className="w-3 h-3" />
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('cart.supplementsLabel')}</label>
              {supplementsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                  <span className="ml-2 text-sm text-gray-900 font-normal">{t('cart.loadingSupplements')}</span>
                </div>
              ) : availableSupplements.length === 0 ? (
                <p className="text-sm text-gray-900 font-normal italic">{t('cart.noSupplementsAvailable')}</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {availableSupplements.map((supplement) => {
                    const isSelected = editSupplements.some(s => s.id === supplement.id);
                    const selectedSupplement = editSupplements.find(s => s.id === supplement.id);
                    
                    return (
                      <div key={supplement.id} className={`flex items-center justify-between p-3 rounded-md transition-all ${
                        isSelected ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSupplementToggle(supplement)}
                            className="mr-3 h-4 w-4 text-[rgb(5, 70, 37)] focus:ring-green-500 border-gray-300 rounded"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-800">{supplement.name}</span>
                            <span className="block text-xs text-[rgb(5, 70, 37)]">+{supplement.price?.toFixed(2) || '0.00'} €</span>
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleSupplementQuantityChange(supplement.id, (selectedSupplement?.quantity || 1) - 1)}
                              disabled={(selectedSupplement?.quantity || 1) <= 1}
                              className="w-6 h-6 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t('cart.decreaseQuantity')}
                            >
                              <FaMinus className="w-3 h-3" />
                            </button>
                            <span className="text-sm font-medium text-gray-800 min-w-[1.5rem] text-center">
                              {selectedSupplement?.quantity || 1}
                            </span>
                            <button
                              onClick={() => handleSupplementQuantityChange(supplement.id, (selectedSupplement?.quantity || 1) + 1)}
                              className="w-6 h-6 rounded-full bg-gray-200 hover:bg-[rgb(5, 70, 37)] hover:text-white transition-colors flex items-center justify-center"
                              title={t('cart.increaseQuantity')}
                            >
                              <FaPlus className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Total des suppléments */}
            {editSupplements.length > 0 && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{t('cart.supplementsTotal')}</span>
                  <span className="text-sm font-semibold text-[rgb(5, 70, 37)]">
                    +{editSupplements.reduce((total, sup) => total + (sup.price * sup.quantity), 0).toFixed(2)} €
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                {t('cart.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="cart-page-cta px-4 py-2 rounded-md font-semibold"
              >
                {t('cart.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
